import { useTranslation } from 'react-i18next';
import Body from '../components/Body';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Typography, keyframes } from '@mui/material';
import MarkdownText from '../components/MarkdownText';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { useCallback, useRef, useState } from 'react';
import Image from '../components/Image';
import LoginDialog from '../forms/LoginDialog';
import { useAuth } from '../data/auth/token';
import Section from '../components/Section';
import Block from '../components/Block';

const question = {
  borderBottom: 'solid 1px #fff2',
  my: 1,
};

const answer = {
  transition: 'all 300ms ease 0s',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

const button = {
  px: 0,
  py: 2,
  fontStyle: 'normal',
  justifyContent: 'flex-start',
  flexDirection: 'row',
  borderRadius: 0,
  textAlign: 'left',

  '.MuiButton-endIcon': {
    flex: 1,
    justifyContent: 'flex-end',
    opacity: 0.5,
  },
};

type QuestionProps = {
  item: {
    title: string;
    description: string;
  };
};

const Question = ({ item }: QuestionProps) => {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement>();

  const handleClick = useCallback(() => {
    if (ref.current?.offsetHeight) {
      setHeight(ref.current.offsetHeight);
    }
    setOpen(state => !state);
  }, []);

  return (
    <Box sx={question}>
      <Typography
        variant="body1"
        sx={button}
        fullWidth
        component={Button}
        onClick={handleClick}
        endIcon={(
          <KeyboardArrowDownIcon sx={{
            transition: 'transform 300ms ease 0s',
            transform: open? 'rotate(180deg)': '',
          }} />
        )}
      >{item.title}</Typography>
      <Box sx={[answer, { maxHeight: open ? height : 0 }]}>
        <Box ref={ref} sx={{ pb: 2 }}>
          <MarkdownText>{item.description}</MarkdownText>
        </Box>
      </Box>
    </Box>
  )
}

const AnimatedTitle = ({ text }) => {
  const words = text.split(' ');

  const fadeIn = keyframes`
    to {
      opacity: 1;
    }
  `;

  return (
    <Typography
      component="h1"
      variant="h1"
      sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
    >
      {words.map((word, index) => (
        <Box
          key={word + index}
          component="span"
          sx={{ 
            opacity: 0,
            ml: 1,
            animation: `${fadeIn} 1s forwards`, 
            animationDelay: `${(0.1 * index) + (index > 0 ? 1 : 0) * 2 }s`
          }}
        >
          {word}
        </Box>
      ))}
    </Typography>
  );
};

const WheelAnimation = ({ x, y, duration, scores, colors, languages }: { scores: { [key: string]: number }, colors: { [key: string]: string }, languages: string[] }) => {
  const items = [...languages].reverse();
  const r = 30;
  return (
    <Box sx={{
      width: '320px',
      height: '0',
      paddingBottom: '320px',
      borderRadius: '160px',
      position: 'relative',
      background: '#38191E',
      margin: '32px auto',
      transformOrigin: '160px 160px',
      animation: `${keyframes`
        0% {
          transform: rotateZ(-360000deg);
        }
        ${x + (y - x) / 2}% {
          transform: rotateZ(0deg);
        }
      `} ${duration + 1}s cubic-bezier(0.595, 0.945, 0.650, 0.985) forwards`,
      '::after': {
        zIndex: 10,
        position: 'absolute',
        content: '""',
        display: 'block',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        backdropFilter: 'blur(40px)',
        boxShadow: '0 0 0 1px red',
        mask: `radial-gradient(farthest-side, #0000 calc(100% - ${r}px), #000 calc(100% - ${r}px))`,
        animation: `${keyframes`
          0% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        `} 500ms linear forwards`,
        animationDelay: `${((x + (y - x) / 2) / 100) * duration + 1}s`,
      },
    }}>
      <Box sx={{
        top: 40,
        left: 40,
        width: '240px',
        height: '240px',
        borderRadius: '120px',
        position: 'absolute',
        background: '#220e1088',
        backdropFilter: 'blur(40px)',
        zIndex: 12,
      }} />
      {items.map((item, i) => {
        const len = items.length;
        const p1 = i / len;
        const p2 = (i + 1) / len;
        const start = items.slice(0, i).reduce((a, l) => a + scores[l], 0);
        const angle = (start / 100) * 360;
        const score = scores[item];
        const color = colors[item];
        const a = `--${item.slice(0, 1)}a`;
        const r = `--${item.slice(0, 1)}r`;
        const series = [
          [80, 5, 5, 5, 5],
          [5, 80, 5, 5, 5],
          [5, 5, 80, 5, 5],
          [5, 5, 5, 80, 5],
          [5, 5, 5, 5, 80],
        ];
        const animationString = series.map((_, j) => `
        ${j * (x / series.length)}% {
          ${a}: ${(1 / len) * 100};
          ${r}: ${(i / len) * 360};
        }
        ${j * (x / series.length) + 1}% {
          ${a}: ${series[j][i]};
          ${r}: ${series[j].slice(0, i).reduce((a, x) => a + x, 0) * 3.6};
        }
        ${(j + 1) * (x / series.length) - 1}% {
          ${a}: ${series[j][i]};
          ${r}: ${series[j].slice(0, i).reduce((a, x) => a + x, 0) * 3.6};
        }`).join('') + `
        ${x}% {
          ${a}: ${(1 / len) * 100};
          ${r}: ${(i / len) * 360};
        }`;

        return (
          <Box key={item} sx={{
            [a]: score,
            [r]: angle,
            width: '320px',
            height: '320px',
            position: 'absolute',
            borderRadius: '160px',
            transformOrigin: '160px 160px',
            background: `conic-gradient(${color} calc(var(${a}) * 1%), #0000 0)`,
            mask: `radial-gradient(farthest-side, #0000 calc(99% - ${r}px), #000 calc(100% - ${r}px))`,
            transform: `rotateZ(calc(var(${r}) * 1deg))`,
            zIndex: len - i + 1,
            animation: `${keyframes`
              ${animationString}
              ${x + 1}% {
                ${a}: 0;
                ${r}: 0;
              }
              ${y}% {
                ${a}: 0;
                ${r}: 0;
              }
              ${y + p1 * (100 - y)}% {
                ${a}: 0;
                ${r}: 0;
              }
              ${y + (p1 + (p2 - p1)/8) * (100 - y)}% {
                ${a}: ${score};
                ${r}: ${angle};
              }
            `} ${duration}s cubic-bezier(0.595, 0.945, 0.650, 0.985) forwards`,
            animationDelay: '1s',
          }} />
        );
      })}
    </Box>
  );
};

export default function LoveLanguages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const x = 20;
  const y = 25;
  const duration = 20;

  const name = searchParams.get('name');
  const pv = parseFloat(searchParams.get('p') || '0');
  const qv = parseFloat(searchParams.get('q') || '0');
  const wv = parseFloat(searchParams.get('w') || '0');
  const av = parseFloat(searchParams.get('a') || '0');
  const gv = parseFloat(searchParams.get('g') || '0');
  const total = pv + qv + wv + av + gv;
  const p = Math.round(100 * pv / total);
  const q = Math.round(100 * qv / total);
  const w = Math.round(100 * wv / total);
  const a = Math.round(100 * av / total);
  const g = Math.round(100 * gv / total);

  const scores = {
    physical_touch: p,
    quality_time: q,
    words_of_affirmation: w,
    acts_of_service: a,
    gifts: g,
  };
  const colors = {
    physical_touch: '#EE5951',
    quality_time: '#77B651',
    words_of_affirmation: '#259FCF',
    acts_of_service: '#FDC451',
    gifts: '#A86EE1',
  };

  const languages = [
    'physical_touch',
    'quality_time',
    'words_of_affirmation',
    'acts_of_service',
    'gifts',
  ].sort((a, b) => scores[b] - scores[a]);
  const pageTitle = t('LoveLanguages.page_title', 'Ditt resultat för kärleksspråk testet');

  const title = name
    ? t('LoveLanguages.title', '{{name}}, här är dina kärleksspråk', { name })
    : t('LoveLanguages.plain_title', 'Här är dina kärleksspråk');

  const fadeIn = keyframes`
    to {
      opacity: 1;
    }
  `;

  const benefits = [
    {
      icon: '/hand.svg',
      description: t('membership_benefit_1.description', 'Chans att matchas och introduceras till rätt personer för just dig'),
    },
    {
      icon: '/heart.svg',
      description: t('membership_benefit_2.description', 'Vid en introduktion planerar vi dejten åt er så ni kan fokusera på att lära känna varandra'),
    },
    {
      icon: '/person.svg',
      description: t('membership_benefit_3.description', 'En diskret profil som endast våra matchmakers har tillgång till'),
    },
    {
      icon: '/flash.svg',
      description: t('membership_benefit_4.description', 'Slipp allt svajpande och chattande, gå direkt på bra dejter istället'),
    },
  ];

  const process = [
    {
      icon: '/one.svg',
      title: t('LoveLanguages_membership_process_1.title', 'Svara på våra frågor'),
      description: t('LoveLanguages_membership_process_1.description', 'Svara på frågor om vem du är och vem du längtar efter. Det är bästa sättet för våra matchmakers att veta vem som är rätt person för just dig.'),
    },
    {
      icon: '/two.svg',
      title: t('LoveLanguages_membership_process_2.title', 'Du är med i urvalet'),
      description: t('LoveLanguages_membership_process_2.description', 'När du har skapat din profil och verifierat dig så är du redo att vara med i urvalet. Våra matchmakers går igenom alla i urvalet och väljer ut dom bästa för nästa steg.'),
    },
    {
      icon: '/three.svg',
      title: t('LoveLanguages_membership_process_3.title', 'Bli introducerad'),
      description: t('LoveLanguages_membership_process_3.description', 'När du blir kontaktad så betyder det att du är utvald för att ni är en riktigt bra match och våra matchmakers vill introducera dig.'),
    },
  ];

  const questions = [
    {
      title: t('membership_question_1.title'),
      description: t('membership_question_1.description'),
    },
    {
      title: t('invite_question_2.title', 'Varför kontaktar ni mig?'),
      description: t('invite_question_2.description', 'En av våra matchmakers tror att den har hittat en bra match till dig! När du svarar på profilformuläret får matchmakern all information som behövs för att utforska vidare er matchningspotential. Om ni sedan introduceras för att gå på en dejt så fixar vi allt åt er, så ni bara kan fokusera på att utforska varandras sällskap.'),
    },
    {
      title: t('invite_question_3.title', 'Kostar detta mig något?'),
      description: t('invite_question_3.description', 'Nej. Det är helt kostnadsfritt att svara på frågorna. När du skickat in dina svar så får du en kostnadsfri medlemsprofil i vårt nätverk med chans att bli matchad med fler av våra klienter. Som nätverksmedlem har du dock inga garantier om introduktioner till matchningar.\n\nFör garanterade introduktioner med utvalda personer som matchar just dig så kan du skaffa en egen personlig matchmaker. Du får ett program med bland annat garanterade matchningar, coaching och stöd från din matchmaker för att kunna ha mer kärlek i din framtid. Att vara klient i matchmakingprogrammet kostar.'),
    },
    {
      title: t('invite_question_4.title', 'Hur hanterar ni min information?'),
      description: t('invite_question_4.description', 'När du skickar in dina svar på formuläret till oss så blir du medlem i Relates matchmaking-nätverk. Vi tar dataskydd, diskression och sekretess på största allvar. Vi använder din data endast för matchningsändamål enligt våra användarvillkor och integritetspolicy och säljer aldrig till tredjeparter.'),
    },
    {
      title: t('invite_question_5.title', 'Kan jag skaffa en egen personlig matchmaker?'),
      description: t('invite_question_5.description', 'Ja! Som klient får du ta del av ett matchmaking-program som hjälper dig att hitta relationen som du drömmer om. Du får bland annat garanterade introduktioner, coaching och stöd från din matchmaker för att hitta rätt i ditt kärleksliv. Att vara klient i matchmaking-programmet kostar och [du kan läsa mer om det här.](/matchmaker)'),
    },
    {
      title: t('membership_question_6.title', 'Hur kan jag kontakta Relate?'),
      description: t('membership_question_6.description', 'Du är välkommen att skriva dina frågor och tankar till Relate på [hello@relate-matchmaking.com](mailto:hello@relate-matchmaking.com).'),
    }
  ];

  const handleClick = useCallback(() => {
    if (!auth?.user) {
      // Open login modal.
      setShowLogin(true);
    } else {
      // Start profile form if logged in.
      const isApplied = Boolean(auth?.user?.appliedAt);
      const isProfileComplete = Boolean(auth?.user?.profileUpdatedAt);
      const isPreferenceComplete = Boolean(auth?.user?.preferenceUpdatedAt);
      // Skip profile selection if none exist already.
      if (!isApplied) {
        navigate('/application');
        return;
      }
      if (!isProfileComplete) {
        navigate('/profile');
        return;
      }
      if (!isPreferenceComplete) {
        navigate('/preferences');
        return;
      }
      navigate('/');
    }
  }, [auth]);

  return (
    <Body>
      <Helmet>
        <title>Relate | {pageTitle}</title>
      </Helmet>
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <Image src="/logo.png" sx={{ width: 65 }} />
      </Box>
      <Box>
        <Box>
          <Section
            sx={{
              textAlign: 'center',
              em: { color: 'white', fontWeight: 'bold' },
            }}
          >
            <Box sx={{ mb: 2 }}>
              <AnimatedTitle text={title} />
              <WheelAnimation x={x} y={y} duration={duration} scores={scores} colors={colors} languages={languages} />
              <Box sx={{ position: 'relative', height: 40 * 5 }}>
                {languages.map((language, i) => {
                  const series = [
                    [-40, -40, -40, -40, 0],
                    [-40, -40, -40, 0, 40],
                    [-40, -40, 0, 40, 80],
                    [-40, 0, 40, 80, 120],
                    [0, 40, 80, 120, 160],
                  ];
                  return (
                    <Box key={language} sx={{
                      mb: 2, display: 'flex', flexDirection: 'row', alignItems: 'center',
                      opacity: 0,
                      position: 'absolute',
                      width: '100%',
                      top: 0,
                      height: 40,
                      animation: `${keyframes`
                        0% {
                          opacity: 0;
                          transform: translateY(-40px);
                        }
                        4.5% {
                          transform: translateY(${series[0][i]}px);
                          opacity: ${series[0][i] >= 0 ? 1 : 0};
                        }
                        20% {
                          transform: translateY(${series[0][i]}px);
                          opacity: ${series[0][i] >= 0 ? 1 : 0};
                        }
                        24.5% {
                          transform: translateY(${series[1][i]}px);
                          opacity: ${series[1][i] >= 0 ? 1 : 0};
                        }
                        40% {
                          transform: translateY(${series[1][i]}px);
                          opacity: ${series[1][i] >= 0 ? 1 : 0};
                        }
                        44.5% {
                          transform: translateY(${series[2][i]}px);
                          opacity: ${series[2][i] >= 0 ? 1 : 0};
                        }
                        60% {
                          transform: translateY(${series[2][i]}px);
                          opacity: ${series[2][i] >= 0 ? 1 : 0};
                        }
                        64.5% {
                          transform: translateY(${series[3][i]}px);
                          opacity: ${series[3][i] >= 0 ? 1 : 0};
                        }
                        80% {
                          transform: translateY(${series[3][i]}px);
                          opacity: ${series[3][i] >= 0 ? 1 : 0};
                        }
                        84.5% {
                          transform: translateY(${series[4][i]}px);
                          opacity: ${series[4][i] >= 0 ? 1 : 0};
                        }
                        100% {
                          transform: translateY(${series[4][i]}px);
                          opacity: ${series[4][i] >= 0 ? 1 : 0};
                        }
                      `} ${((100 - y) / 100) * duration}s forwards`,
                      animationDelay: `${(y / 100) * duration + 1}s`,
                    }}>
                      <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: 8,
                        background: colors[language],
                        mr: 1,
                        flex: 0,
                        flexBasis: 16,
                      }} />
                      <Typography component="h3" variant="h3" sx={{ mb: 0, flex: 1, textAlign: 'left' }}>{t(`LoveLanguages.${language}.name`)}</Typography>
                      <Typography component="h3" variant="h3" sx={{ mb: 0, flex: 0, textAlign: 'right' }}>{scores[language]}%</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{
                opacity: 0,
                animation: `${fadeIn} 500ms forwards linear`,
                animationDelay: `${duration}s`,
              }}
            >
              {languages.map(language => (
                <Block key={language}  sx={{ mx: 0, p: { xs: 3, sm: 4 } }}>
                  <Box key={language} sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 8,
                      background: colors[language],
                      mr: 1,
                    }} />
                    <Typography component="h3" variant="h3" sx={{ mb: 0 }}>{t(`LoveLanguages.${language}.name`) + ` (${scores[language]}%)`}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <MarkdownText>
                      {t(`LoveLanguages.${language}.description`)}
                    </MarkdownText>
                  </Box>
                </Block>
              ))}
            </Box>
          </Section>
        </Box>
        <Box sx={{ borderTop: 'solid 1px #38191E' }}></Box>
        <Section
          sx={{
            textAlign: 'center',
            em: { color: 'white', fontWeight: 'bold' },
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography component="h2" variant="h1" sx={{ mb: 4 }}>
              {name
                ? t('membership.title', '{{name}}, du är inbjuden att gå med i vårt nätverk helt kostnadsfritt ♥️', { name })
                : t('membership.plain_title', 'Du är inbjuden att gå med i vårt nätverk helt kostnadsfritt ♥️')}
            </Typography>
            <Typography variant="body1">
              {t('LoveLanguages_membership.not_ready_description', 'Som medlem har du tillgång till massor av värdefulla resurser som hjälper dig hitta rätt utifrån dina kärleksspråk. Du har också chansen att bli matchad med och introducerad till våra klienter som längtar efter samma saker som du. Du behöver bara svara på våra matchmakers frågor och sedan luta dig tillbaka.')}
            </Typography>
          </Box>
          <Button
            onClick={handleClick}
            fullWidth
            size="large"
            variant="contained"
            sx={{
              mb: 1,
              backgroundAttachment: 'fixed',
              backgroundImage:  'repeating-radial-gradient(circle at 100px -400px, #76451F66 0, transparent 800px), repeating-linear-gradient(#be8a6144, #be8a6122)',
            }}
          >
              {t('membership.action', 'Gå med i vårt nätverk')}
          </Button>
          <Typography variant="caption" component="div" textAlign="center" sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex'}}>
            {t('membership.help', 'Medlemskap i vårt nätverk är kostnadsfritt.')}
          </Typography>
        </Section>
        <Section highlight sx={{ flexDirection: 'row' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Typography component="h2" variant="h2" textAlign="center">
              {t('membership.benefits_title', 'Som medlem får du')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              {benefits.map((item) => (
                <Box key={item.icon} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                  <Box
                    component="img"
                    src={item.icon}
                    sx={{
                      width: 52, height: 52, minWidth: 52, mr: 2,
                      borderRadius: 26,
                      backgroundAttachment: 'fixed',
                      backgroundImage:  'repeating-radial-gradient(circle at 0 0, #230e11 0, transparent 200px), repeating-linear-gradient(#4a362b88, #4a362b44)',
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>{item.description}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              width: '40%',
              background: 'url(/membership.png)',
              backgroundPosition: 'left center',
              backgroundSize: 'auto 100%',
              ml: 6,
              mr: -6,
              mt: -6,
              mb: -6,
              borderTopRightRadius: '32px',
              borderBottomRightRadius: '32px',
              display: 'none',
              '@media screen and (min-width: 720px)': { display: 'flex' },
            }}
          />
        </Section>
        <Section>
          <Typography component="h2" variant="h2" textAlign="center">
            {t('LoveLanguages_membership.process_title', 'Så går det till')}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {t('LoveLanguages_membership.process_description', 'Detta är hur det går till för dig som inbjuden till nätverket:')}
          </Typography>
          <Box>
            {process.map((item) => (
              <Box key={item.icon} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 6 }}>
                <Box
                  component="img"
                  src={item.icon}
                  sx={{
                    width: 52, height: 52, minWidth: 52, mb: 2,
                    borderRadius: 26,
                    backgroundAttachment: 'fixed',
                    backgroundImage:  'repeating-radial-gradient(circle at 0 0, #230e11 0, transparent 100px), repeating-linear-gradient(#4a362b88, #4a362b44)',
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, flex: 1, flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography component="h3" variant="h3">{item.title}</Typography>
                  <Typography variant="body2" textAlign="center">{item.description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Section>
        <Section highlight sx={{ alignItems: 'center', flexDirection: 'row' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" textAlign="center" fontStyle="italic" sx={{ mb: 1 }}>
              “{t('membership.quote', 'Mycket roligare att bli kontaktad av en matchmaker och personligen bli inbjuden till en dejt, än att bara få en notifikation från en app. Det kändes verkligen som att man hittade så många stunder där vi båda svarade “Så tänker jag också!” Här stämde så många saker redan från början kändes det som.')}”
            </Typography>
            <Typography component="h5" variant="h5" textAlign="center">
              {t('membership.quote_author', '– Medlem i nätverket')}
            </Typography>
          </Box>
          <Box 
            component="img" 
            src="/erik.png" 
            sx={{
              width: 192, height: 192, ml: 4,
              borderRadius: 96,
              display: 'none',
              '@media screen and (min-width: 720px)': { display: 'flex' },
            }}
          />
        </Section>
        <Section>
          <Box sx={{ pb: 4, borderBottom: 'solid 1px #fff2' }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography component="h2" variant="h2" textAlign="center">
                {t('LoveLanguages.selling_title', 'Svara på frågorna idag')}
              </Typography>
              <MarkdownText>
                {t('LoveLanguages.selling_description', 'Vi söker kontinuerligt efter matchningar för våra klienter, svara på frågorna och gå med i vårt nätverk för att få möjligheten att bli introducerad.\n\nDet kostar inget och du slipper dejtingappar - du behöver bara svara på vår matchmakers frågor.')}
              </MarkdownText>
            </Box>
            <Button
              onClick={handleClick}
              fullWidth
              size="large"
              variant="contained"
              sx={{
                mb: 1,
                backgroundAttachment: 'fixed',
                backgroundImage:  'repeating-radial-gradient(circle at 10px 0, #76451F66 0, transparent 600px), repeating-linear-gradient(#be8a6144, #be8a6122)',
              }}
            >
              {t('LoveLanguages.action', 'Svara på frågorna idag')}
            </Button>
            <Typography variant="caption" component="div" textAlign="center" sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex'}}>
              <WatchLaterIcon sx={{ fontSize: 17, mr: '4px' }} />
              {t('membership.help', 'Medlemskap i vårt nätverk är kostnadsfritt.')}
            </Typography>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Box sx={{ pb: 3 }}>
              <Typography component="h2" variant="h2" textAlign="center">
                {t('LoveLanguages_membership.matchmaking_title', 'Vill du också ha en egen personlig matchmaker?')}
              </Typography>
              <Typography variant="body2" textAlign="center">
                {t('membership.matchmaking_description', 'Med din egna personlig matchmaker går du igenom en noggrann matchmakingprocess där du bland annat får garanterade introduktioner med utvalda matchningar samt tips, stöd och verktyg av din matchmaker.')}
              </Typography>
            </Box>
            <Button
              component={Link}
              to={'/matchmaker' + (name ? `?name=${name}`: '')}
              fullWidth
              size="large"
              variant="outlined"
              sx={{
                mb: 1,
                backgroundAttachment: 'fixed',
                backgroundImage:  'repeating-radial-gradient(circle at 0 0, #230e11 0, transparent 600px), repeating-linear-gradient(#4a362b88, #4a362b44)',
              }}
            >
              {t('membership.matchmaking_action', 'Läs mer om vårt matchmakingprogam')}
            </Button>
          </Box>
        </Section>
        <Section highlight>
          <Typography component="h2" variant="h2">
            {t('LoveLanguages.questions_title', 'Undrar du något?')}
          </Typography>
          <Box>
            {questions.map((item) => (
              <Question key={item.title} item={item} />
            ))}
          </Box>
        </Section>
        <Section sx={{ alignItems: 'center' }}>
          <Image src="/logo.png" sx={{ width: 84 }} />
          <Typography variant="caption">
            {t('slogan')}
          </Typography>
        </Section>
        <Section />
        {showLogin && (
          <LoginDialog
            open
            handleClose={() => setShowLogin(false)}
            onLoggedIn={(user) => {
              const isApplied = Boolean(user?.appliedAt);
              const isProfileComplete = Boolean(user?.profileUpdatedAt);
              const isPreferenceComplete = Boolean(user?.preferenceUpdatedAt);
              // Skip profile selection if none exist already.
              if (!isApplied) {
                navigate('/application');
                return;
              }
              if (!isProfileComplete) {
                navigate('/profile');
                return;
              }
              if (!isPreferenceComplete) {
                navigate('/preferences');
                return;
              }
              navigate('/');
            }}
          />
        )}
      </Box>
    </Body>
  );
}
